import type { DocumentNode } from '@apollo/client';
import { useState } from 'react';

import { EngagementGovernanceDecision } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import ApproveActions, {
  type ExpandedMode,
} from '../components/GovernanceApproverInbox/ApproveActions';
import { formatEngagementMoney, parseDollarsToCents } from './money';
import { useGovernanceDecision } from './useGovernanceDecision';

/**
 * The one currency `submitGovernanceDecision` takes a proposed ceiling in. The
 * api stores every engagement amount in USD, and `validateGovernanceInput`
 * refuses anything else with `engagement.governance_invalid_nte`.
 *
 * The engagement's display currency is not a stand-in for it. Since ENG-5774 a
 * credit-funded engagement shows its NTE in credits (`OFC`): passing that
 * through sends a request the api always refuses, and relabelling the figure the
 * approver typed as USD would propose a ceiling in the wrong unit. The pricing
 * engine only routes USD-invoiced engagements to governance (over the threshold,
 * `funding_method` is `usd_invoice`), so the check below normally passes; it
 * keeps the adjustment off anything else instead of sending a request that
 * cannot succeed.
 */
const PROPOSAL_CURRENCY = 'USD';

interface GovernanceDecisionControlsProps {
  engagementId: string;
  /**
   * The currency the engagement's NTE is shown in, or null when none resolved.
   * An adjustment is offered only when it is `PROPOSAL_CURRENCY`.
   */
  currency: string | null;
  expandedMode: ExpandedMode | null;
  onExpand: (mode: ExpandedMode | null) => void;
  /** What the host surface reads the engagement from; see `useGovernanceDecision`. */
  refetchQueries: DocumentNode[];
  onDecided?: () => void;
}

/**
 * Approve, Approve with adjustment and Reject with note, with the two expanders
 * behind them. The one implementation of the governance decision, rendered by
 * the Approvals row and the Hub's governance banner alike (ENG-5752).
 *
 * Returns siblings rather than a wrapper so each host lays them out in its own
 * gap: the row's card and the banner space their children differently.
 */
export default function GovernanceDecisionControls({
  engagementId,
  currency,
  expandedMode,
  onExpand,
  refetchQueries,
  onDecided,
}: GovernanceDecisionControlsProps): JSX.Element {
  const { decide, pending, busy, error } = useGovernanceDecision({
    engagementId,
    refetchQueries,
    onDecided,
  });
  const canAdjust = currency === PROPOSAL_CURRENCY;

  return (
    <>
      <ApproveActions
        expandedMode={expandedMode}
        busy={busy}
        approving={pending === EngagementGovernanceDecision.Approve}
        canAdjust={canAdjust}
        onApprove={() => void decide(EngagementGovernanceDecision.Approve)}
        onExpand={onExpand}
      />

      {canAdjust ? null : (
        <p className="text-on-surface-muted body-2">
          Adjustments are proposed in US dollars, and this engagement’s not-to-exceed total isn’t in
          dollars. To ask for a different ceiling, reject with a note.
        </p>
      )}

      {expandedMode === 'approve_adjustment' && canAdjust ? (
        <AdjustmentForm
          busy={busy}
          sending={pending === EngagementGovernanceDecision.Adjust}
          onSend={(cents, note) =>
            void decide(EngagementGovernanceDecision.Adjust, {
              proposedNteTotal: { cents: String(cents), currency: PROPOSAL_CURRENCY },
              note: note || undefined,
            })
          }
        />
      ) : null}

      {expandedMode === 'reject_note' ? (
        <RejectionForm
          busy={busy}
          sending={pending === EngagementGovernanceDecision.Reject}
          onSend={(note) => void decide(EngagementGovernanceDecision.Reject, { note })}
        />
      ) : null}

      {/* `role="alert"`: this lands after an async attempt, outside the host's
          live region, and is the only sign the decision did not go through. */}
      {error ? (
        <p role="alert" className="text-alert-error body-2 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </>
  );
}

interface DecisionFormProps {
  busy: boolean;
  /** This form's decision is the one in flight. */
  sending: boolean;
}

/**
 * Each expander owns its own note. They used to share one, so an optional
 * comment typed on the adjustment became the rejection reason — already filled
 * in, with Send rejection enabled — the moment the approver switched to reject.
 */
function AdjustmentForm({
  busy,
  sending,
  onSend,
}: DecisionFormProps & { onSend: (cents: number, note: string) => void }) {
  const [total, setTotal] = useState('');
  const [note, setNote] = useState('');
  const cents = parseDollarsToCents(total);
  const typed = total.trim() !== '';

  return (
    <div className="flex flex-col gap-8">
      <Input
        label="Proposed not-to-exceed total (USD)"
        value={total}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotal(e.target.value)}
        error={typed && cents === null}
        // Read back what will be sent, so no ceiling goes out that the approver
        // did not see written as money.
        helperText={
          cents !== null
            ? `Proposing ${formatEngagementMoney(cents, PROPOSAL_CURRENCY)}`
            : typed
              ? 'Enter a dollar amount, such as 50,000.'
              : undefined
        }
      />
      <Input
        label="Note (optional)"
        value={note}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
      />
      <div>
        <Button
          size="small"
          className="min-h-48"
          disabled={cents === null || busy}
          loading={sending}
          onClick={() => {
            if (cents !== null) onSend(cents, note.trim());
          }}
        >
          Send adjustment
        </Button>
      </div>
    </div>
  );
}

function RejectionForm({
  busy,
  sending,
  onSend,
}: DecisionFormProps & { onSend: (note: string) => void }) {
  const [note, setNote] = useState('');
  // The server enforces `engagement.governance_note_required`; keeping the
  // client check means the approver finds out before the round-trip.
  const valid = note.trim().length > 0;

  return (
    <div className="flex flex-col gap-8">
      <Input
        label="Why are you rejecting this?"
        value={note}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
        error={note.length > 0 && !valid}
        helperText="Required — the requester sees this."
      />
      <div>
        <Button
          size="small"
          className="min-h-48"
          disabled={!valid || busy}
          loading={sending}
          onClick={() => onSend(note.trim())}
        >
          Send rejection
        </Button>
      </div>
    </div>
  );
}
